import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export interface ScopeBarProps {
    orgLabel: string;
    includeChildren: boolean;
    onToggleIncludeChildren: () => void;
    /** Back to the tree, where the branch can actually be changed. */
    backHref: string;
}

/**
 * EAA-1820 (frame 35 header): the org-only "Phạm vi" bar — the branch chip and
 * "Gồm toàn bộ nhánh con" checkbox. Real per the ticket's own governing rule
 * ("Khác nhau chỉ ở ngữ cảnh: 35 có thanh Phạm vi") — the realm screen has no
 * scope to narrow (it already reads the whole realm), so `AccountsScreen` only
 * renders this when the adapter supplies it.
 *
 * The chip's ✕ navigates back to the org tree rather than clearing a filter:
 * the scope org rides in the URL path segment of
 * `GET /organizations/{id}/accounts`, not a query param, so there is no
 * "no scope" state on this screen short of leaving it — see
 * `useScopedAccounts`'s docstring.
 */
export function ScopeBar({ orgLabel, includeChildren, onToggleIncludeChildren, backHref }: ScopeBarProps) {
    const { t } = useTranslation();
    return (
        <div className="acc-scope-bar">
            <span className="acc-scope-bar__label">{t('console.accounts.scopeBar.label')}</span>
            <span className="acc-scope-bar__chip">
                {orgLabel}
                <Link
                    to={backHref}
                    className="acc-scope-bar__remove"
                    aria-label={t('console.accounts.scopeBar.removeAriaLabel', { scope: orgLabel })}
                    title={t('console.accounts.scopeBar.backToTreeTitle')}
                >
                    <svg
                        viewBox="0 0 24 24"
                        width="12"
                        height="12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2.2}
                        aria-hidden="true"
                    >
                        <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />
                    </svg>
                </Link>
            </span>
            <label className="acc-scope-bar__toggle">
                <input
                    type="checkbox"
                    className="rm-check"
                    checked={includeChildren}
                    onChange={onToggleIncludeChildren}
                />
                {t('console.accounts.scopeBar.includeChildrenLabel')}
            </label>
        </div>
    );
}

export default ScopeBar;
